import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Line, Doughnut, Bar } from 'react-chartjs-2';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  ChartOptions,
  Filler,
  ArcElement,
  BarElement
} from 'chart.js';
import { useApp } from '../context/AppContext';
import { topAndTail, formatDateTime } from '../utils/stringUtils';
import StatCard from './StatCard';
import ChartCard from './ChartCard';
import IdentityDetailsModal from './IdentityDetailsModal';
import { countValidationRules, countXIdentityFields } from '../utils/channelUtils';
import { DeleteButton } from './shared/DeleteButton';

// Register Chart.js components
ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Title,
  Tooltip,
  Legend,
  Filler,
  ArcElement,
  BarElement
);

const Dashboard: React.FC = () => {
  const { state, actions, getCurrentStats } = useApp();
  const navigate = useNavigate();
  const [showDidModal, setShowDidModal] = useState(false);
  const [didDocument, setDidDocument] = useState<any>(null);
  const [showIdentityModal, setShowIdentityModal] = useState(false);
  const [selectedIdentity, setSelectedIdentity] = useState<any>(null);
  
  // Bucket selector for aggregated connections graph
  const [selectedBucket, setSelectedBucket] = useState<number | null>(null);
  const previousBucketRef = React.useRef<number | null>(null);
  
  // Get the currently active stats (global or filtered)
  const stats = getCurrentStats();

  // Clear any dashboard filters when viewing main dashboard (only once on mount)
  React.useEffect(() => {
    actions.setDashboardFilters(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Run only once on mount

  // Sync selectedBucket with settings on mount and when settings change
  React.useEffect(() => {
    if (state.settings?.bucket_seconds && selectedBucket === null) {
      setSelectedBucket(state.settings.bucket_seconds);
    }
  }, [state.settings?.bucket_seconds, selectedBucket]);

  // Show DID document modal
  const showDidDocumentModal = (didDoc: any) => {
    setDidDocument(didDoc);
    setShowDidModal(true);
  };

  // Show identity details modal
  const showIdentityDetails = (identity: any) => {
    setSelectedIdentity(identity);
    setShowIdentityModal(true);
  };

  const [channelChartView, setChannelChartView] = useState<'pie' | 'bar' | 'connections'>('pie');
  const [pipeChartView, setPipeChartView] = useState<'pie' | 'bar' | 'requests'>('pie');
  const [isRefreshing, setIsRefreshing] = React.useState(false);

  // Handler for bucket selector change
  const handleBucketChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    const newBucket = parseInt(event.target.value, 10);
    setSelectedBucket(newBucket);
    actions.setDashboardBucketOverride(newBucket);
    
    // Only trigger refresh if bucket actually changed
    if (previousBucketRef.current !== newBucket) {
      previousBucketRef.current = newBucket;
      // Use setTimeout to ensure state has updated
      setTimeout(() => {
        actions.loadDashboardStats(true);
      }, 0);
    }
  };

  // Debug: Log when dashboard stats change
  React.useEffect(() => {
    // console.log('Dashboard stats updated:', {
    //   totalConnections: stats?.metrics?.total_connections,
    //   timeSeriesLength: stats?.metrics?.time_series?.length,
    //   latestPoint: stats?.metrics?.time_series?.[stats.metrics.time_series.length - 1]
    // });
  }, [stats?.metrics]);

  // Prepare connections chart data - memoized to avoid recalculating on every render
  const connectionsChartData = useMemo(() => {
    if (!stats?.metrics?.time_series) {
      return {
        labels: [],
        datasets: []
      };
    }

    const data = stats.metrics.time_series;
    
    // Convert timestamps to local time
    const labels = data.map(point => {
      const date = new Date(point.timestamp);
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    });
    
    // Define all possible datasets
    const allDatasets = [
      {
        label: 'Gateway Faults',
        data: data.map(point => point.gateway_faults || 0),
        borderColor: '#e74a3b',
        backgroundColor: 'rgba(231, 74, 59, 0.1)',
        borderWidth: 1,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Connections',
        data: data.map(point => point.count || 0),
        borderColor: '#4e73df',
        backgroundColor: 'rgba(78, 115, 223, 0.1)',
        borderWidth: 1,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Rule Accepts',
        data: data.map(point => point.rule_accepts || 0),
        borderColor: '#1cc88a',
        backgroundColor: 'rgba(28, 200, 138, 0.1)',
        borderWidth: 1,
        fill: true,
        tension: 0.4,
      },
      {
        label: 'Rule Denies',
        data: data.map(point => point.rule_denies || 0),
        borderColor: '#9966ff',
        backgroundColor: 'rgba(153, 102, 255, 0.1)',
        borderWidth: 1,
        fill: true,
        tension: 0.4,
      }
    ];
    
    // Split datasets into those with data and those without, but hide empty ones from rendering
    const datasets = allDatasets.map(dataset => ({
      ...dataset,
      hidden: !dataset.data.some(value => value > 0)
    }));

    return {
      labels,
      datasets
    };
  }, [stats?.metrics?.time_series]);

  // Prepare channel distribution chart data
  const getChannelDistributionData = () => {
    if (!stats?.channels || stats.channels.length === 0) {
      //console.log('Channel Distribution: No channels data', { hasStats: !!stats, channelsLength: stats?.channels?.length });
      return { labels: [], datasets: [] };
    }

    const channelData = stats.channels.map(channel => ({
      name: channel.name,
      connections: channel.accept_count || 0,
      rules: channel.rule_count || 0,
      total_connections: channel.accept_count || 0,
      successful: channel.accept_count || 0,
      failed: channel.deny_count || 0,
      gateway_faults: channel.gateway_faults || 0
    }));
    
    const totalConnections = channelData.reduce((sum, ch) => sum + ch.total_connections, 0);
    const hasTraffic = totalConnections > 0;
    
    // Sort by activity (connections or rules) and take top 10
    const sortedChannels = channelData
      .map(ch => ({
        ...ch,
        activityValue: hasTraffic ? ch.total_connections : ch.rules
      }))
      .filter(ch => ch.activityValue > 0) // Only show channels with data
      .sort((a, b) => b.activityValue - a.activityValue) // Most active first
      .slice(0, 10); // Maximum 10 channels
    
    // console.log('Channel Distribution Data:', { 
    //   totalChannels: channelData.length,
    //   displayedChannels: sortedChannels.length,
    //   totalConnections,
    //   hasTraffic,
    //   channels: sortedChannels 
    // });

    const colors = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#858796', '#5a5c69', '#2e59d9', '#17a673', '#2c9faf'];

    const dataToDisplay = sortedChannels.map(r => r.activityValue);

    if (channelChartView === 'pie') {
      return {
        labels: sortedChannels.map(r => r.name),
        datasets: [{
          data: dataToDisplay,
          backgroundColor: colors.slice(0, sortedChannels.length),
          borderWidth: 0
        }]
      };
    } else if (channelChartView === 'bar') {
      return {
        labels: sortedChannels.map(r => r.name),
        datasets: [{
          label: hasTraffic ? 'Connections' : 'Rules',
          data: dataToDisplay,
          backgroundColor: '#4e73df',
          borderColor: '#4e73df',
          borderWidth: 1
        }]
      };
    } else { // connections view
      return {
        labels: sortedChannels.map(r => r.name),
        datasets: [{
          label: 'Total Connections',
          data: sortedChannels.map(r => r.total_connections),
          backgroundColor: '#1cc88a',
          borderColor: '#1cc88a',
          borderWidth: 1
        }, {
          label: 'Rules Count',
          data: sortedChannels.map(r => r.rules),
          backgroundColor: '#36b9cc',
          borderColor: '#36b9cc',
          borderWidth: 1
        }]
      };
    }
  };

  // Prepare pipe distribution chart data
  const getPipeDistributionData = () => {
    const pipeStats = stats?.metrics?.pipe_stats || [];
    if (pipeStats.length === 0) {
      return { labels: [], datasets: [] };
    }

    // Sort by total requests and take top 10
    const sortedPipes = [...pipeStats]
      .filter(pipe => pipe.total_requests > 0)
      .sort((a, b) => b.total_requests - a.total_requests)
      .slice(0, 10);

    const colors = ['#4e73df', '#1cc88a', '#36b9cc', '#f6c23e', '#e74a3b', '#858796', '#5a5c69', '#2e59d9', '#17a673', '#2c9faf'];

    if (pipeChartView === 'pie') {
      return {
        labels: sortedPipes.map(p => `${p.pipe_name || 'Unnamed'} (${p.provider})`),
        datasets: [{
          data: sortedPipes.map(p => p.total_requests),
          backgroundColor: colors.slice(0, sortedPipes.length),
          borderWidth: 0
        }]
      };
    } else if (pipeChartView === 'bar') {
      return {
        labels: sortedPipes.map(p => `${p.pipe_name || 'Unnamed'}`),
        datasets: [{
          label: 'Total Requests',
          data: sortedPipes.map(p => p.total_requests),
          backgroundColor: '#1cc88a',
          borderColor: '#1cc88a',
          borderWidth: 1
        }]
      };
    } else { // requests view - show successful vs failed
      return {
        labels: sortedPipes.map(p => `${p.pipe_name || 'Unnamed'}`),
        datasets: [{
          label: 'Successful',
          data: sortedPipes.map(p => p.successful_requests),
          backgroundColor: '#1cc88a',
          borderColor: '#1cc88a',
          borderWidth: 1
        }, {
          label: 'Failed',
          data: sortedPipes.map(p => p.failed_requests),
          backgroundColor: '#e74a3b',
          borderColor: '#e74a3b',
          borderWidth: 1
        }]
      };
    }
  };

  // Prepare latency chart data - memoized to avoid recalculating on every render
  const latencyChartData = useMemo(() => {
    if (!stats?.metrics?.latency_time_series) {
      return {
        labels: [],
        datasets: []
      };
    }

    const data = stats.metrics.latency_time_series;
    
    // Convert timestamps to local time
    const labels = data.map((point) => {
      const date = new Date(point.timestamp);
      return date.toLocaleTimeString(undefined, {
        hour: '2-digit',
        minute: '2-digit',
        hour12: false
      });
    });

    return {
      labels,
      datasets: [
        {
          label: 'p50 (Median)',
          data: data.map((point) => point.p50_latency_ms || 0),
          borderColor: '#1cc88a',
          backgroundColor: 'rgba(28, 200, 138, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: false,
          tension: 0.3,
        },
        {
          label: 'p95',
          data: data.map((point) => point.p95_latency_ms || 0),
          borderColor: '#f6c23e',
          backgroundColor: 'rgba(246, 194, 62, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: false,
          tension: 0.3,
        },
        {
          label: 'p99',
          data: data.map((point) => point.p99_latency_ms || 0),
          borderColor: '#e74a3b',
          backgroundColor: 'rgba(231, 74, 59, 0.1)',
          borderWidth: 2,
          pointRadius: 2,
          pointHoverRadius: 4,
          fill: false,
          tension: 0.3,
        },
        {
          label: 'Average',
          data: data.map((point) => point.avg_latency_ms || 0),
          borderColor: '#4e73df',
          backgroundColor: 'rgba(78, 115, 223, 0.1)',
          borderWidth: 1,
          pointRadius: 1,
          pointHoverRadius: 3,
          fill: false,
          tension: 0.3,
          borderDash: [5, 5],
        }
      ]
    };
  }, [stats?.metrics?.latency_time_series]);

  // Chart options for different chart types
  const doughnutChartOptions: ChartOptions<'doughnut'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        position: 'bottom',
      },
    },
    animation: {
      duration: 500,
      easing: 'easeInOutQuart'
    }
  };

  const barChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: channelChartView === 'connections',
      },
    },
    scales: {
      y: {
        beginAtZero: true,
      },
    },
  };

  const latencyChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (context) => {
            // Get the original timestamp from the data
            const dataIndex = context[0].dataIndex;
            const timestamp = stats?.metrics?.latency_time_series?.[dataIndex]?.timestamp;
            if (timestamp) {
              const date = new Date(timestamp);
              return date.toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
              });
            }
            return context[0].label;
          }
        }
      },
    },
    scales: {
      x: {
        display: true,
        title: {
          display: true,
          text: 'Time'
        }
      },
      y: {
        display: true,
        title: {
          display: true,
          text: 'Latency (ms)'
        },
        beginAtZero: true
      }
    },
    interaction: {
      mode: 'nearest',
      axis: 'x',
      intersect: false
    }
  };

  const connectionsChartOptions: ChartOptions<'line'> = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          boxWidth: 8,
          boxHeight: 8,
          padding: 15
        }
      },
      title: {
        display: false
      },
      tooltip: {
        mode: 'index',
        intersect: false,
        callbacks: {
          title: (context) => {
            // Get the original timestamp from the data
            const dataIndex = context[0].dataIndex;
            const timestamp = stats?.metrics?.time_series?.[dataIndex]?.timestamp;
            if (timestamp) {
              const date = new Date(timestamp);
              return date.toLocaleString(undefined, {
                month: 'short',
                day: 'numeric',
                hour: '2-digit',
                minute: '2-digit',
                hour12: false,
                timeZone: Intl.DateTimeFormat().resolvedOptions().timeZone
              });
            }
            return context[0].label;
          }
        }
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      }
    },
    animation: {
      duration: 300,
      easing: 'easeInOutQuart'
    },
    transitions: {
      active: {
        animation: {
          duration: 0
        }
      }
    }
  };

  // Prepare identity-channel chart data
  const getIdentityChannelChartData = () => {
    if (!stats?.metrics?.identity_channel_stats || stats.metrics.identity_channel_stats.length === 0) {
      return {
        labels: [],
        datasets: []
      };
    }

    const identityChannelStats = stats.metrics.identity_channel_stats;
    
    // Create a map of config_id to channel name
    const configIdToName: Record<string, string> = {};
    stats.channels.forEach(channel => {
      if (channel.config_id) {
        configIdToName[channel.config_id] = channel.name;
      }
    });
    
    // Create a set of channel names that have duplicates
    const channelNameCounts: Record<string, number> = {};
    const duplicateChannelNames = new Set<string>();
    
    stats.channels.forEach(channel => {
      channelNameCounts[channel.name] = (channelNameCounts[channel.name] || 0) + 1;
    });
    
    Object.entries(channelNameCounts).forEach(([name, count]) => {
      if (count > 1) {
        duplicateChannelNames.add(name);
      }
    });
    
    // Aggregate data by identity hash
    const identityMap: Record<string, { channels: Record<string, { success: number; deny: number; fault: number; }>; total: number; }> = {};
    
    identityChannelStats.forEach(stat => {
      if (!identityMap[stat.identity_hash]) {
        identityMap[stat.identity_hash] = {
          channels: {},
          total: 0
        };
      }
      
      // Map config_id to channel name for display
      const channelName = configIdToName[stat.channel_config_id] || stat.channel_config_id;
      identityMap[stat.identity_hash].channels[channelName] = {
        success: stat.success_count,
        deny: stat.deny_count,
        fault: stat.fault_count
      };
      identityMap[stat.identity_hash].total += stat.total_count;
    });

    // Sort identities by total count (descending), then by identity hash alphabetically
    const sortedIdentities = Object.entries(identityMap)
      .sort((a, b) => {
        // Primary sort: by total connections (descending)
        const totalDiff = b[1].total - a[1].total;
        if (totalDiff !== 0) return totalDiff;
        
        // Secondary sort: by identity hash alphabetically (ascending)
        return a[0].localeCompare(b[0]);
      })
      .slice(0, 10);

    // Build labels (shortened identity hash + channel)
    const labels: string[] = [];
    const successData: number[] = [];
    const denyData: number[] = [];
    const faultData: number[] = [];

    sortedIdentities.forEach(([identity, data]) => {
      const shortIdentity = identity.substring(0, 8) + '...';
      // Sort channels alphabetically for each identity
      const sortedChannels = Object.entries(data.channels).sort((a, b) => a[0].localeCompare(b[0]));
      
      sortedChannels.forEach(([channel, counts]) => {
        // Show channel name with warning if there are multiple channels with the same name
        const displayName = duplicateChannelNames.has(channel) 
          ? `${channel} ⚠️` 
          : channel;
        labels.push(`${shortIdentity} → ${displayName}`);
        successData.push(counts.success);
        denyData.push(counts.deny);
        faultData.push(counts.fault);
      });
    });

    return {
      labels,
      datasets: [{
        label: 'Success',
        data: successData,
        backgroundColor: 'rgba(28, 200, 138, 0.8)',
        borderColor: 'rgba(28, 200, 138, 1)',
        borderWidth: 1
      }, {
        label: 'Denied',
        data: denyData,
        backgroundColor: 'rgba(246, 194, 62, 0.8)',
        borderColor: 'rgba(246, 194, 62, 1)',
        borderWidth: 1
      }, {
        label: 'Gateway Fault',
        data: faultData,
        backgroundColor: 'rgba(231, 74, 59, 0.8)',
        borderColor: 'rgba(231, 74, 59, 1)',
        borderWidth: 1
      }]
    };
  };

  const identityChannelChartOptions: ChartOptions<'bar'> = {
    responsive: true,
    maintainAspectRatio: false,
    indexAxis: 'y',
    plugins: {
      legend: {
        position: 'top',
        display: true
      },
      tooltip: {
        mode: 'index',
        intersect: false
      }
    },
    scales: {
      x: {
        stacked: true,
        beginAtZero: true,
        ticks: {
          precision: 0
        }
      },
      y: {
        stacked: true
      }
    },
    animation: {
      duration: 500,
      easing: 'easeInOutQuart'
    }
  };

  const handleSettingsClick = () => {
    navigate('/settings');
  };

  const handleManualRefresh = async () => {
    setIsRefreshing(true);
    try {
      await actions.loadDashboardStats();
    } finally {
      setIsRefreshing(false);
    }
  };

  if (state.isLoading.dashboard && !stats) {
    return (
      <div className="container-fluid">
        <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '400px' }}>
          <div className="text-center">
            <div className="spinner-border text-primary" role="status">
              <span className="sr-only">Loading...</span>
            </div>
            <p className="mt-2 text-muted">Loading dashboard...</p>
          </div>
        </div>
      </div>
    );
  }

  // Show error state if there's an error
  if (state.error) {
    return (
      <div className="container-fluid">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="card border-left-danger shadow">
              <div className="card-body">
                <div className="text-center">
                  <i className="fas fa-exclamation-triangle text-danger fa-3x mb-3"></i>
                  <h4 className="text-gray-800">Dashboard Error</h4>
                  <p className="text-gray-600 mb-4">{state.error}</p>
                  <button 
                    className="btn btn-primary"
                    onClick={handleManualRefresh}
                    disabled={isRefreshing}
                  >
                    <i className={`fas fa-sync-alt ${isRefreshing ? 'fa-spin' : ''} mr-2`}></i>
                    Try Again
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // stats is already declared at the top of the component

  return (
    <div className="container-fluid">{/* Statistics Cards Row */}
      <div id="statistics-section"></div>
      <div className="row">
        <StatCard
          title="Total Agent Identities"
          value={stats?.total_identities ?? 0}
          icon="fa-users"
          color="primary"
          onClick={() => navigate('/identities')}
          subtitle={`${stats?.identities?.filter(i => i.badge === 'NEW').length || 0} new`}
        />
        
        <StatCard
          title="Channels"
          value={stats?.channels?.length ?? 0}
          icon="fa-route"
          color="success"
          onClick={() => navigate('/channels')}
          badge={(() => {
            const faultCount = stats?.channels?.filter(r => r.last_status === 'fault' || r.last_status === 'gateway_fault').length || 0;
            return faultCount > 0 ? {
              text: `${faultCount} FAULT${faultCount > 1 ? 'S' : ''}`,
              color: 'danger' as const
            } : undefined;
          })()}
        />
        
        <StatCard
          title="Total Connections"
          value={stats?.metrics?.total_connections ?? 0}
          icon="fa-exchange-alt"
          color="info"
          subtitle={`Last ${stats?.metrics?.connections_window_minutes ?? 60} minutes`}
          onClick={handleSettingsClick}
        />
        
        {/* Combined Latency Card */}
        <div className="col-xl-3 col-md-6 mb-4">
          <div 
            className="card stat-card success shadow h-100 py-2 clickable"
            onClick={handleSettingsClick}
            style={{ cursor: 'pointer' }}
          >
            <div className="card-body">
              <div className="row no-gutters align-items-center">
                <div className="col mr-2">
                  <div className="text-xs font-weight-bold text-success text-uppercase mb-1">
                    Avg Latency
                    <small 
                      className="text-muted d-block" 
                      style={{ 
                        fontSize: '0.65rem', 
                        fontWeight: 'normal', 
                        textTransform: 'none', 
                        cursor: 'pointer' 
                      }}
                      title="Click to change settings"
                    >
                      Last {stats?.metrics?.latency_window_minutes ?? 60} min
                    </small>
                  </div>
                  <div className="mb-2">
                    <div className="d-flex align-items-center mb-1">
                      <i className="fas fa-arrow-right text-success mr-2" style={{ fontSize: '0.875rem' }}></i>
                      <span className="text-xs text-muted mr-2">Request:</span>
                      <span className="h6 mb-0 font-weight-bold text-gray-800">
                        {stats?.metrics?.avg_request_latency ? `${stats.metrics.avg_request_latency.toFixed(2)}ms` : 'N/A'}
                      </span>
                    </div>
                    <div className="d-flex align-items-center">
                      <i className="fas fa-arrow-left text-warning mr-2" style={{ fontSize: '0.875rem' }}></i>
                      <span className="text-xs text-muted mr-2">Response:</span>
                      <span className="h6 mb-0 font-weight-bold text-gray-800">
                        {stats?.metrics?.avg_response_latency ? `${stats.metrics.avg_response_latency.toFixed(2)}ms` : 'N/A'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="col-auto">
                  <i className="fas fa-clock fa-2x text-gray-300"></i>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pipe Metrics Row */}
      {stats?.metrics?.pipe_stats && stats.metrics.pipe_stats.length > 0 && (
        <div className="row">
          <StatCard
            title="Active LLM Pipes"
            value={stats.metrics.pipe_stats.length}
            icon="fa-brain"
            color="success"
            onClick={() => navigate('/tasks')}
            subtitle={`${stats.metrics.pipe_stats.filter((p: any) => p.total_requests > 0).length} with traffic`}
          />
          
          {/* Pipe Latency Card */}
          <div className="col-xl-3 col-md-6 mb-4">
            <div 
              className="card stat-card info shadow h-100 py-2"
              onClick={() => navigate('/tasks')}
              style={{ cursor: 'pointer' }}
              title="Click to view pipe tasks"
            >
              <div className="card-body">
                <div className="row no-gutters align-items-center">
                  <div className="col mr-2">
                    <div className="text-xs font-weight-bold text-info text-uppercase mb-1">
                      Pipe Latency
                      <small 
                        className="text-muted d-block" 
                        style={{ 
                          fontSize: '0.65rem', 
                          fontWeight: 'normal', 
                          textTransform: 'none' 
                        }}
                      >
                        Last {stats?.metrics?.latency_window_minutes ?? 60} min
                      </small>
                    </div>
                    <div className="mb-2">
                      <div className="d-flex align-items-center mb-1">
                        <i className="fas fa-microchip text-info mr-2" style={{ fontSize: '0.875rem' }}></i>
                        <span className="text-xs text-muted mr-2">Gateway:</span>
                        <span className="h6 mb-0 font-weight-bold text-gray-800">
                          {stats?.metrics?.avg_pipe_gateway_processing_ms ? `${stats.metrics.avg_pipe_gateway_processing_ms.toFixed(2)}ms` : 'N/A'}
                        </span>
                      </div>
                      <div className="d-flex align-items-center">
                        <i className="fas fa-brain text-primary mr-2" style={{ fontSize: '0.875rem' }}></i>
                        <span className="text-xs text-muted mr-2">LLM Target:</span>
                        <span className="h6 mb-0 font-weight-bold text-gray-800">
                          {stats?.metrics?.avg_pipe_target_processing_ms ? `${stats.metrics.avg_pipe_target_processing_ms.toFixed(2)}ms` : 'N/A'}
                        </span>
                      </div>
                    </div>
                  </div>
                  <div className="col-auto">
                    <i className="fas fa-tachometer-alt fa-2x text-gray-300"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          <StatCard
            title="Total Tokens"
            value={(() => {
              const total = stats.metrics.pipe_stats.reduce((sum: number, p: any) => sum + p.total_input_tokens + p.total_output_tokens, 0);
              if (total >= 1000000) return `${(total / 1000000).toFixed(2)}M`;
              if (total >= 1000) return `${(total / 1000).toFixed(1)}K`;
              return total.toString();
            })()}
            icon="fa-coins"
            color="info"
            onClick={() => navigate('/tasks')}
            subtitle="Combined I/O"
          />
          
          <StatCard
            title="Total Cost"
            value={`$${stats.metrics.pipe_stats.reduce((sum: number, p: any) => sum + p.total_cost, 0).toFixed(4)}`}
            icon="fa-dollar-sign"
            color="danger"
            onClick={() => navigate('/tasks')}
            subtitle={`Avg: ${stats.metrics.pipe_stats.length > 0 ? (stats.metrics.pipe_stats.reduce((sum: number, p: any) => sum + p.avg_latency_ms, 0) / stats.metrics.pipe_stats.length).toFixed(0) : 0}ms`}
          />
        </div>
      )}

      {/* Charts Row */}
      <div id="metrics-section"></div>
      <div id="connections-section"></div>
      <div className="row">
        <ChartCard 
          title="Aggregated Connections Over Time"
          headerActions={
            <select
              className="form-control form-control-sm dropdown-styling"
              style={{ width: 'auto' }}
              value={selectedBucket || state.globalState.currentBucketSeconds || state.settings?.bucket_seconds || 30}
              onChange={handleBucketChange}
            >
              <option value={30}>30 seconds</option>
              <option value={60}>1 minute</option>
              <option value={300}>5 minutes</option>
              <option value={900}>15 minutes</option>
              <option value={1800}>30 minutes</option>
              <option value={3600}>1 hour</option>
              <option value={10800}>3 hours</option>
              <option value={21600}>6 hours</option>
            </select>
          }
        >
          <div style={{ height: '300px' }}>
            <Line 
              data={connectionsChartData} 
              options={connectionsChartOptions}
              redraw={false}
            />
          </div>
        </ChartCard>
      </div>

      {/* Channel Stats and Source-Destination Charts Row */}
      <div className="row">
        {/* Channel Distribution */}
        <div className="col-lg-6 mb-4">
          <div className="card shadow h-100">
            <div className="card-header py-3 d-flex justify-content-between align-items-center">
              <h6 className="m-0 font-weight-bold text-primary">
                <i className="fas fa-chart-pie"></i> Channel Distribution
              </h6>
              <div className="btn-group btn-group-sm" role="group">
                <button 
                  type="button" 
                  className={`btn btn-outline-primary ${channelChartView === 'pie' ? 'active' : ''}`}
                  onClick={() => setChannelChartView('pie')}
                >
                  Pie
                </button>
                <button 
                  type="button" 
                  className={`btn btn-outline-primary ${channelChartView === 'bar' ? 'active' : ''}`}
                  onClick={() => setChannelChartView('bar')}
                >
                  Bar
                </button>
                <button 
                  type="button" 
                  className={`btn btn-outline-primary ${channelChartView === 'connections' ? 'active' : ''}`}
                  onClick={() => setChannelChartView('connections')}
                >
                  Connections
                </button>
              </div>
            </div>
            <div className="card-body">
              <div style={{ height: '300px' }}>
                {channelChartView === 'pie' ? (
                  <Doughnut data={getChannelDistributionData()} options={doughnutChartOptions} />
                ) : (
                  <Bar data={getChannelDistributionData()} options={barChartOptions} />
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Identity Connections */}
        <div className="col-lg-6 mb-4">
          <div className="card shadow h-100">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                <i className="fas fa-fingerprint"></i> Identity Connections by Channel
              </h6>
            </div>
            <div className="card-body">
              <div style={{ height: '300px' }}>
                {getIdentityChannelChartData().labels.length > 0 ? (
                  <Bar 
                    data={getIdentityChannelChartData()}
                    options={identityChannelChartOptions}
                  />
                ) : (
                  <div className="d-flex justify-content-center align-items-center h-100">
                    <p className="text-muted">No identity-channel data available</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Pipe Distribution Row */}
      {stats?.metrics?.pipe_stats && stats.metrics.pipe_stats.length > 0 && (
        <div className="row">
          <div className="col-lg-12 mb-4">
            <div className="card shadow h-100">
              <div className="card-header py-3 d-flex justify-content-between align-items-center">
                <h6 className="m-0 font-weight-bold text-success">
                  <i className="fas fa-brain"></i> LLM Pipe Distribution
                </h6>
                <div className="btn-group btn-group-sm" role="group">
                  <button 
                    type="button" 
                    className={`btn btn-outline-success ${pipeChartView === 'pie' ? 'active' : ''}`}
                    onClick={() => setPipeChartView('pie')}
                  >
                    Pie
                  </button>
                  <button 
                    type="button" 
                    className={`btn btn-outline-success ${pipeChartView === 'bar' ? 'active' : ''}`}
                    onClick={() => setPipeChartView('bar')}
                  >
                    Bar
                  </button>
                  <button 
                    type="button" 
                    className={`btn btn-outline-success ${pipeChartView === 'requests' ? 'active' : ''}`}
                    onClick={() => setPipeChartView('requests')}
                  >
                    Success/Fail
                  </button>
                </div>
              </div>
              <div className="card-body">
                <div style={{ height: '300px' }}>
                  {pipeChartView === 'pie' ? (
                    <Doughnut data={getPipeDistributionData()} options={doughnutChartOptions} />
                  ) : (
                    <Bar data={getPipeDistributionData()} options={barChartOptions} />
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Latency Stats Row */}
      <div id="latency-section"></div>
      <div className="row">
        <ChartCard 
          title="Connection Latency Over Time (p50, p95, p99)"
          headerActions={
            <select
              className="form-control form-control-sm dropdown-styling"
              style={{ width: 'auto' }}
              value={selectedBucket || state.globalState.currentBucketSeconds || state.settings?.bucket_seconds || 30}
              onChange={handleBucketChange}
            >
                <option value={30}>30 seconds</option>
                <option value={60}>1 minute</option>
                <option value={300}>5 minutes</option>
                <option value={900}>15 minutes</option>
                <option value={1800}>30 minutes</option>
                <option value={3600}>1 hour</option>
                <option value={10800}>3 hours</option>
                <option value={21600}>6 hours</option>
            </select>
          }
        >
          <div style={{ height: '300px' }}>
            <Line data={latencyChartData} options={latencyChartOptions} redraw={false} />
          </div>
        </ChartCard>
      </div>

      
      {/* Configuration Section */}
      <div id="configuration-section"></div>
      <div className="row">
        <div className="col-lg-12 mb-4">
          <div className="card shadow">
            <div className="card-header py-3">
              <h6 className="m-0 font-weight-bold text-primary">
                <i className="fas fa-cog"></i> Gateway Configuration
              </h6>
            </div>
            <div className="card-body">
              <div className="row">
                <div className="col-md-6">
                  <h6>Identity & DID</h6>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Proxy DID:</strong></td>
                        <td>
                          <code className="text-sm">
                            {stats?.proxy_info?.did ? (
                              <span 
                                title={`${stats.proxy_info.did} (click to view DID document)`}
                                onClick={() => showDidDocumentModal(stats.proxy_info.did_document)}
                                style={{ cursor: 'pointer', textDecoration: 'underline', color: '#007bff' }}
                              >
                                {stats.proxy_info.did.substring(0, 40)}...
                              </span>
                            ) : (
                              'Not configured'
                            )}
                          </code>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Extensions:</strong></td>
                        <td>
                          <span className={`badge ${
                            stats?.proxy_info?.extensions_enabled ? 'badge-success' : 'badge-secondary'
                          }`}>
                            {stats?.proxy_info?.extensions_enabled ? 'ENABLED' : 'DISABLED'}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>WebSocket Status:</strong></td>
                        <td>
                          <span className={`badge ${
                            state.wsStatus === 'CONNECTED' ? 'badge-success' :
                            state.wsStatus === 'CONNECTING' ? 'badge-secondary' :
                            state.wsStatus === 'ERROR' ? 'badge-danger' : 'badge-warning'
                          }`}>
                            {state.wsStatus}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                </div>
                <div className="col-md-6">
                  <h6>System Status</h6>
                  <table className="table table-sm">
                    <tbody>
                      <tr>
                        <td><strong>Active Channels:</strong></td>
                        <td>
                          <span className="text-muted">
                            {stats?.channels ? (
                              `${stats.channels.filter(r => r.last_status === 'success').length}/${stats.channels.length} recently active`
                            ) : (
                              'No channels'
                            )}
                          </span>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Total Identities:</strong></td>
                        <td>
                          <span className="text-muted">{stats?.total_identities || 0}</span>
                        </td>
                      </tr>
                      <tr>
                        <td><strong>Server Uptime:</strong></td>
                        <td>
                          <span className="text-muted">
                            {stats?.proxy_info?.uptime_formatted || 'Unknown'}
                          </span>
                        </td>
                      </tr>
                    </tbody>
                  </table>
                  
                  <div className="mt-3">
                    <button 
                      className="btn btn-sm btn-outline-primary mr-2"
                      onClick={actions.reloadChannels}
                      title="Reload all proxy channel configurations"
                    >
                      <i className="fas fa-sync-alt"></i> Reload Configuration
                    </button>
                    <DeleteButton
                      onDelete={async () => { await actions.truncateMetrics(); }}
                      className="mr-2"
                      title="Clear all stored metrics data"
                      variant="warning"
                    />
                    <button 
                      className="btn btn-sm btn-outline-info mr-2"
                      onClick={() => navigate('/settings')}
                      title="Configure dashboard settings"
                    >
                      <i className="fas fa-cog"></i> Settings
                    </button>
                    <button 
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => navigate('/logs')}
                      title="View system logs"
                    >
                      <i className="fas fa-file-alt"></i> Logs
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* DID Document Modal */}
      {showDidModal && (
        <div className="modal fade show" style={{ display: 'block' }} tabIndex={-1}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">
                  <i className="fas fa-id-card"></i> DID Document
                </h5>
                <button 
                  type="button" 
                  className="close" 
                  onClick={() => setShowDidModal(false)}
                >
                  <span>&times;</span>
                </button>
              </div>
              <div className="modal-body">
                <pre><code>{didDocument ? JSON.stringify(didDocument, null, 2) : 'No DID document available'}</code></pre>
              </div>
              <div className="modal-footer">
                <button 
                  type="button" 
                  className="btn btn-secondary" 
                  onClick={() => setShowDidModal(false)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
      {showDidModal && <div className="modal-backdrop fade show"></div>}

      {/* Identity Details Modal */}
      {showIdentityModal && selectedIdentity && (
        <IdentityDetailsModal 
          identity={selectedIdentity}
          onClose={() => setShowIdentityModal(false)}
        />
      )}
    </div>
  );
};

export default Dashboard;